import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import { CompanyService } from '@/services/companyService';
import { useCompany } from '@/hooks/useCompany';
import { resetActiveCompanyCache } from '@/services/invoiceGeneration';

const companyService = new CompanyService();

/** `pendingKey` while switching to the platform admin view (a switch with no company id). */
export const PLATFORM_ADMIN_KEY = 'platform-admin';

export interface SwitchCompanyOptions {
  /** Where to land after switching, instead of the view's default page. */
  redirectTo?: string;
}

/**
 * Switches the active company, or enters the platform admin view when `companyId` is null.
 * Resolves true on success. A second call while one is in flight is ignored.
 */
export function useSwitchCompany() {
  const t = useTranslations('consultantShell.changeCompany');
  const navigate = useNavigate();
  const { refetch: refetchCompany } = useCompany();
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const inFlight = useRef(false);

  const switchTo = useCallback(
    async (companyId: string | null, companyName?: string, options: SwitchCompanyOptions = {}): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      setPendingKey(companyId ?? PLATFORM_ADMIN_KEY);
      try {
        await companyService.switchCompany(companyId);

        // In-app navigation (no reload): modules that memoized the old active company id
        // must be invalidated explicitly.
        resetActiveCompanyCache();
        await refetchCompany();

        toast.success(companyId === null ? t('switchedToAdminView') : t('switchedTo', { name: companyName ?? '' }));
        navigate(options.redirectTo ?? (companyId === null ? '/review' : '/overview'), { replace: true });
        return true;
      } catch (err) {
        console.error('Error switching company:', err);
        toast.error(t('switchFailed'));
        return false;
      } finally {
        inFlight.current = false;
        setPendingKey(null);
      }
    },
    [navigate, refetchCompany, t],
  );

  return { switchTo, pendingKey };
}
